import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from "@chakra-ui/react";
import { VisualSupport } from "domain/entities/visual-supports.entities";
import { HiDownload, HiHeart, HiOutlineHeart } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "ui/hook/store";
import { translate } from "ui/i18n";
import { Snackbar } from "ui/pages/snackbar";
import { useWindowWidth } from "ui/utils/hooks";
import { CustomTextTooltip } from "ui/pages/app/components/CustomTextTooltip";

export function VisualSupportBanner({
  support,
  isFavorite,
  handleFavoriteClick,
  handleDownloadClick,
}: {
  support: VisualSupport;
  isFavorite?: boolean;
  handleFavoriteClick?: (e: any) => Promise<void>;
  handleDownloadClick: (e: any, type: "pdf" | "png") => Promise<void>;
}) {
  const isDesktop = useWindowWidth();
  const popoverState = useDisclosure();

  return (
    <div
      className="relative w-full rounded-[20px] bg-secondaryGrey-500"
      style={{ aspectRatio: isDesktop ? "1080/210" : "280/164" }}
    >
      {isDesktop && (
        <div className="absolute right-6 top-6 hidden space-x-4 md:flex">
          {handleFavoriteClick && typeof isFavorite === "boolean" && (
            <CustomTextTooltip
              text={translate(
                `ns1:Activities.Tooltips.${
                  isFavorite ? "RemoveFromFavorites" : "AddToFavorites"
                }`
              )}
            >
              <button
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
                onClick={handleFavoriteClick}
              >
                {isFavorite ? (
                  <HiHeart className="text-secondaryGrey-900" size={20} />
                ) : (
                  <HiOutlineHeart
                    className="text-secondaryGrey-900"
                    size={20}
                  />
                )}
              </button>
            </CustomTextTooltip>
          )}
          <CustomTextTooltip
            text={translate("ns1:Activities.Tooltips.Download")}
            hide={popoverState.isOpen}
          >
            <div>
              <Popover
                isOpen={popoverState.isOpen}
                onOpen={popoverState.onOpen}
                onClose={popoverState.onClose}
                placement="bottom-start"
              >
                <PopoverTrigger>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                      popoverState.isOpen ? "bg-darkGrey-200" : "bg-white"
                    }`}
                  >
                    <HiDownload
                      size={20}
                      className={`${
                        popoverState.isOpen
                          ? "text-darkGrey-700"
                          : "text-secondaryGrey-900"
                      }`}
                    />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  style={{
                    isolation: "isolate",
                    zIndex: 100,
                    width: "fit-content",
                    border: "none",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  }}
                >
                  <div className="flex flex-col rounded-[20px] bg-white px-2 py-3 text-sm font-semibold text-darkGrey-700 shadow-lg">
                    <button
                      className="flex rounded-[10px] px-3 py-2 hover:bg-darkGrey-100"
                      onClick={(e) => handleDownloadClick(e, "pdf")}
                    >
                      <HiDownload
                        className="me-2 text-secondaryGrey-900"
                        size={20}
                      />
                      Pdf
                    </button>
                    <button
                      className="flex rounded-[10px] px-3 py-2 hover:bg-darkGrey-100"
                      onClick={(e) => handleDownloadClick(e, "png")}
                    >
                      <HiDownload
                        className="me-2 text-secondaryGrey-900"
                        size={20}
                      />
                      Png
                    </button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </CustomTextTooltip>
        </div>
      )}
      <div className="items-bottom absolute bottom-6 flex w-full space-x-6 px-6 text-2xl text-secondaryGrey-900">
        <span className="font-bold">{support.title}</span>
      </div>
    </div>
  );
}
